import { useMemo } from 'react';
import { useMsmeProfileQuery, useMsmeStatsQuery } from './queries/useMsmeWorkspaceQueries';
import { deriveOnboardingSteps, type OnboardingProgress, type OnboardingStep } from '../utils/sustainabilityUx';
import { getStoredMsmeReportGoal } from '../constants/msmeSimplifiedUi';
import { firstIncompleteMyCompanyPage } from '../utils/myCompanyRequired';

export interface MsmeSetupProgress {
  loading: boolean;
  progress: OnboardingProgress;
  nextStep: OnboardingStep | null;
  hasReportGoal: boolean;
  myCompanyComplete: boolean;
  nextMyCompanyPath: string | null;
}

export function useMsmeSetupProgress(enabled = true): MsmeSetupProgress {
  const profileQuery = useMsmeProfileQuery(enabled);
  const statsQuery = useMsmeStatsQuery(enabled);

  return useMemo(() => {
    const msmeData = profileQuery.data?.data;
    const statsData = (statsQuery.data as { data?: Record<string, unknown> } | undefined)?.data
      ?? (statsQuery.data as Record<string, unknown> | undefined);

    const carbon = statsData?.carbon as { totalCO2Emissions?: number; currentScore?: number } | undefined;
    const bulkUpload = statsData?.documentBulkUpload as
      | { totalDocuments?: number; totalTransactions?: number }
      | undefined;

    const carbonEmissionsKg = Number(carbon?.totalCO2Emissions || 0);
    const carbonScore = Number(carbon?.currentScore || 0);
    const documentCount = Number(bulkUpload?.totalDocuments ?? 0);
    const documentCarbonSummary = statsData?.documentCarbonSummary as { totals?: unknown } | undefined;
    const hasDocuments =
      documentCount > 0
      || Number(bulkUpload?.totalTransactions ?? 0) > 0
      || Boolean(documentCarbonSummary?.totals)
      || Number((statsData?.documents as { total?: number } | undefined)?.total ?? 0) > 0
      || Number((statsData?.transactions as { total?: number } | undefined)?.total ?? 0) > 0;
    const hasAssessment = carbonEmissionsKg > 0 || carbonScore > 0;
    const exportActivity = (msmeData?.manufacturingProfile as { exportActivity?: boolean } | undefined)?.exportActivity;
    const hasProfile = Boolean(
      msmeData?.companyName
      && msmeData?.gstNumber
      && typeof exportActivity === 'boolean'
    );
    const serverReportGoal = (msmeData as { uxPreferences?: { reportingIntent?: string } } | undefined)
      ?.uxPreferences?.reportingIntent;
    const hasReportGoal = Boolean(getStoredMsmeReportGoal(serverReportGoal));

    const progress = deriveOnboardingSteps({
      hasProfile,
      hasDocuments,
      hasAssessment,
      hasReportGoal,
    });
    const nextCompanyPage = firstIncompleteMyCompanyPage(msmeData);

    return {
      loading: profileQuery.isLoading || statsQuery.isLoading,
      progress,
      nextStep: progress.steps.find((step) => !step.complete) ?? null,
      hasReportGoal,
      myCompanyComplete: !nextCompanyPage,
      nextMyCompanyPath: nextCompanyPage?.path ?? null,
    };
  }, [profileQuery.data, profileQuery.isLoading, statsQuery.data, statsQuery.isLoading]);
}
